import { useEffect, useState, type ReactNode } from 'react';
import { Building2, LineChart, RefreshCw, Ruler } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';

type Benefit = {
  icon: LucideIcon;
  title: string;
  body: string;
};

/** Slide dwell time — long enough to read ~30 words without feeling stalled. */
const ROTATE_INTERVAL_MS = 7000;

const BENEFITS: Benefit[] = [
  {
    icon: Building2,
    title: 'One source of measured truth',
    body: 'Floor plans, stacking plans, tenant rosters, and rentable area for every building in the portfolio — together, in one place.',
  },
  {
    icon: RefreshCw,
    title: 'Current without chasing it',
    body: 'When a tenant moves, our Occupant Services team revises the drawings and the recalculated areas land here, usually within the hour.',
  },
  {
    icon: Ruler,
    title: 'BOMA-compliant by default',
    body: 'Forty years of measurement methodology from the team that co-authored the standard the industry runs on.',
  },
  {
    icon: LineChart,
    title: 'Square footage you were missing',
    body: 'Portfolio analytics flag discrepancies and quantify rentable-area opportunity the moment it appears.',
  },
];

type AuthBenefitsCarouselProps = {
  /** Rendered opposite the slide indicators, sharing their row (the marketing CTA). */
  action?: ReactNode;
};

type CarouselDotProps = {
  index: number;
  isActive: boolean;
  title: string;
  onSelect: (index: number) => void;
};

/**
 * Rotating value propositions for the pre-login marketing panel.
 *
 * Auto-advance pauses while the pointer or keyboard focus is inside the
 * carousel, and never starts at all under `prefers-reduced-motion` — the
 * indicator buttons remain the manual control in every case.
 */
export function AuthBenefitsCarousel({ action }: AuthBenefitsCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // A timer is a genuine side effect. It is torn down whenever the carousel
  // is paused so a hovering reader never loses the slide they are on.
  useEffect(() => {
    if (isPaused) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % BENEFITS.length);
    }, ROTATE_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [isPaused]);

  function handlePause() {
    setIsPaused(true);
  }

  function handleResume() {
    setIsPaused(false);
  }

  return (
    <div
      role="group"
      aria-roledescription="carousel"
      aria-label="Why teams use TruSpace"
      onMouseEnter={handlePause}
      onMouseLeave={handleResume}
      onFocus={handlePause}
      onBlur={handleResume}
    >
      <div className="grid rounded-lg border border-border bg-muted/40 p-6">
        {BENEFITS.map((benefit, index) => {
          const Icon = benefit.icon;
          const isActive = index === activeIndex;
          return (
            <div
              key={benefit.title}
              className={cn(
                'col-start-1 row-start-1 transition-opacity duration-[var(--duration-slow)]',
                isActive ? 'opacity-100' : 'pointer-events-none opacity-0',
              )}
              aria-hidden={!isActive}
            >
              <Icon className="h-8 w-8 text-primary" aria-hidden="true" />
              <h2 className="mt-4 text-xl font-semibold text-foreground">
                {benefit.title}
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {benefit.body}
              </p>
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex items-center justify-between gap-4">
        {action ?? <span />}
        <div className="flex items-center gap-2">
          {BENEFITS.map((benefit, index) => (
            <CarouselDot
              key={benefit.title}
              index={index}
              isActive={index === activeIndex}
              title={benefit.title}
              onSelect={setActiveIndex}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function CarouselDot({ index, isActive, title, onSelect }: CarouselDotProps) {
  function handleClick() {
    onSelect(index);
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={title}
      aria-current={isActive ? 'true' : undefined}
      className={cn(
        // 44px hit area (a11y.mdc §2) around a small visual dot: the padding
        // is the target, the inner span is what the user sees.
        'flex h-11 w-6 items-center justify-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      )}
    >
      <span
        className={cn(
          'h-2 w-4 rounded-sm transition-colors duration-[var(--duration-fast)]',
          isActive ? 'bg-primary' : 'bg-border',
        )}
      />
    </button>
  );
}
